/**
 * i18n (I18N-01/02, OD-12): ВСЕ строки интерфейса — из словарей локалей;
 * в компонентах hardcoded-строк нет (владелец языка — словари). Язык = данные:
 * добавление языка = новый словарь, без правок компонентов и API.
 *
 * Локали: RU (пилот) + EN + полный FR/DE/ES/PT/PL; архитектурно — все
 * европейские (BCP-47 primary tag = код словаря). Форматы дат/чисел/плюрализация
 * — ТОЛЬКО через `Intl` (никакой ручной сборки строк с датами).
 *
 * Выбор языка: автоопределение `navigator.language` при первом входе,
 * переключатель в КАБИНЕТЕ; сохранение — localStorage + профиль пользователя
 * (`POST /me/preferences`). Fallback-цепочка: locale → en → ru.
 */

import ru from "./locales/ru.json";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import es from "./locales/es.json";
import pt from "./locales/pt.json";
import pl from "./locales/pl.json";

export type Lang = "ru" | "en" | "fr" | "de" | "es" | "pt" | "pl";

export const LANGS: Lang[] = ["ru", "en", "fr", "de", "es", "pt", "pl"];

type Dict = Record<string, string>;
const DICTS: Record<Lang, Dict> = { ru, en, fr, de, es, pt, pl };
const STORAGE_KEY = "nika_lang";

let current: Lang = "ru";
const listeners: Array<(lang: Lang) => void> = [];

function isLang(v: string | null): v is Lang {
  return !!v && (LANGS as string[]).includes(v);
}

function detect(): Lang {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (isLang(saved)) return saved;
  const nav = (navigator.language || "ru").slice(0, 2).toLowerCase();
  return isLang(nav) ? nav : "ru";
}

export function initI18n(): Lang {
  current = detect();
  return current;
}

export function getLang(): Lang {
  return current;
}

export function setLang(lang: Lang): void {
  current = lang;
  localStorage.setItem(STORAGE_KEY, lang);
  document.documentElement.lang = lang;
  for (const fn of listeners) fn(lang);
}

export function onLangChange(fn: (lang: Lang) => void): void {
  listeners.push(fn);
}

/** Ключ → строка словаря; {param} — интерполяция. Fallback: locale → en → ru. */
export function t(key: string, params?: Record<string, string | number>): string {
  const raw = DICTS[current][key] ?? DICTS.en[key] ?? DICTS.ru[key] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name: string) =>
    name in params ? String(params[name]) : m);
}

/**
 * Плюрализация через `Intl.PluralRules` (BCP-47): формы лежат в словаре как
 * `<key>.one|few|many|other` (сколько требует язык).
 */
export function plural(key: string, n: number): string {
  const category = new Intl.PluralRules(current).select(n);
  return t(`${key}.${category}`, { n: fmtNumber(n) });
}

// ── Форматы — только `Intl` (I18N-02) ────────────────────────────────────────

export function fmtNumber(n: number, opts?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(current, opts).format(n);
}

export function fmtWeight(kg: number): string {
  return new Intl.NumberFormat(current, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(kg);
}

export function fmtDate(iso: string | null | undefined,
                        opts: Intl.DateTimeFormatOptions = {
                          dateStyle: "medium",
                        }): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(current, opts).format(d);
}

export function fmtDateTime(iso: string | null | undefined): string {
  return fmtDate(iso, { dateStyle: "medium", timeStyle: "short" });
}
